const { log, warn } = require("./PortalLogging");
const PortalStorage = require("./PortalStorage");
const PortalEvent = require("./PortalEvent");

/**
 * url(pathParts=[], ...queryParamObjects={}) {
 *
 * Generate URIs in a standardized(-ish XD) way
 */
function url(pathParts = "/", ...queryParamObjects) {
  let ret = Array.isArray(pathParts)
    ? pathParts.join("/").replace(/\/\/+/, "/")
    : pathParts;
  ret = ret.replace(/^(https?:)/, "$1/");
  const queryParams = Object.assign({}, ...queryParamObjects);
  const qs = Object.keys(queryParams)
    .map(
      (k) =>
        k +
        "=" +
        encodeURIComponent(
          typeof queryParams[k] === "object"
            ? JSON.stringify(queryParams[k])
            : queryParams[k]
        )
    )
    .join("&");
  if (qs) ret += "?" + qs;
  return ret;
}

module.exports = class PortalAPI {
  constructor(opts) {
    ["endpoint"].forEach((prop) => {
      if (opts[prop] === undefined)
        throw new Error(`Required prop '${prop}' missing for PortalAPI`);
    });

    log("new PortalAPI", opts);

    Object.assign(this, opts);
    opts._storeInSessionTimeout = null;
    this.cache = new Map();
  }

  absolutePath(...segments) {
    const queries = segments.filter((x) => typeof x === "object");
    segments = segments.filter((x) => typeof x !== "object");
    const path = ["", this.pathname, ...segments].join("/");
    if (queries.length === 0) return path;
    else return url(path, ...queries);
  }

  retrieveCurrentSession(token) {
    //start spinner
    PortalEvent.emit("showSpinner");
    return new Promise((resolve, reject) => {
      let param = {token: token};
      fetch(url([this.endpoint, "session"], param), { credentials: "same-origin" })
      .then((resp) => (resp.ok ? resp.json() : resp.text().then(reject)))
      .then((data) => {
        PortalEvent.emit("hideSpinner"); // Hide spinner
        return resolve(data);
      })
      .catch((error) => {
        PortalEvent.emit("hideSpinner"); // Hide spinner
        return reject(error);
      });
    });
  }

  startSession(lang = "de-DE") {
    //start spinner
    PortalEvent.emit("showSpinner");
    return new Promise((resolve, reject) => {
      //     Sprache bei Session nicht übergeben, weil sonst andere Datensprachen
      //     von easydb "abgewählt" werden.
      //     Stattdessen Sprache bei Suche übergeben.
      //      fetch(url([this.endpoint, 'session'], {language: lang}), {credentials: 'same-origin'})
      fetch(url([this.endpoint, "session"]), { credentials: "same-origin" })
        .then((resp) => (resp.ok ? resp.json() : resp.text().then(reject)))
        .then((data) => {
          PortalEvent.emit("hideSpinner"); // Hide spinner
          return resolve(data);
        })
        .catch((error) => {
          PortalEvent.emit("hideSpinner"); // Hide spinner
          return reject(error);
        });
    });
  }

  authenticate(token, username, password) {
    //start spinner
    PortalEvent.emit("showSpinner");
    if (token) {
      return new Promise((resolve, reject) => {
        let param = { token: token };
        if (username == "anonymous") {
          param.method = `anonymous`;
        } else {
          if (username && password) {
            param.login = username;
            param.password = password;
          } else {
            param.login = this.apiuser;
            param.password = this.apipass;
          }
        }
        fetch(url([this.endpoint, "session/authenticate"], param), {
          credentials: "same-origin",
          method: "POST",
        })
          .then((resp) => (resp.ok ? resp.json() : resp.text().then(reject)))
          .then((data) => {
            // ToDo: pending_messages verarbeiten
            PortalEvent.emit("hideSpinner"); // Hide spinner
            return resolve(data);
          })
          .catch((error) => {
            PortalEvent.emit("hideSpinner"); // Hide spinner
            return reject(error);
          });
      });
    } else {
      PortalEvent.emit("hideSpinner"); // Hide spinner
      return Promise.reject("missing token"); // Hide spinner
    }
  }

  getMessages(groups) {
    //start spinner
    PortalEvent.emit("showSpinner");
    let token = PortalStorage.getTokenFromCookies();
    // Check if token is null and show an alert
    if (token == null) {
      return Promise.reject("missing token");
    }
    return new Promise((resolve, reject) => {
      let timestamp = new Date().toISOString();
      let searchRequest = {
        type: "message",
        search: [
          {
            type: "in",
            in: ["maintenance_warning", "maintenance"],
            fields: ["message.webfrontend_type"],
          },
          {
            type: "complex",
            search: [
              {
                bool: "should",
                type: "in",
                fields: ["message.end_time"],
                in: [null],
              },
              {
                bool: "should",
                type: "range",
                field: "message.end_time",
                from: timestamp,
              },
            ],
          },
          {
            type: "complex",
            search: [
              {
                bool: "should",
                type: "in",
                fields: ["message.start_time"],
                in: [null],
              },
              {
                bool: "should",
                type: "range",
                field: "message.start_time",
                to: timestamp,
              },
            ],
          },
          { type: "in", fields: ["_groups.group._id"], in: groups },
        ],
        sort: [{ field: "message.start_time" }, { field: "message.title" }],
      };
      fetch(url([this.endpoint, "search"], { token: token }), {
        credentials: "same-origin",
        method: "POST",
        body: JSON.stringify(searchRequest),
      })
        .then((resp) => (resp.ok ? resp.json() : resp.text().then(reject)))
        .then((data) => {
          PortalEvent.emit("hideSpinner"); // Hide spinner
          return resolve(data);
        })
        .catch((error) => {
          PortalEvent.emit("hideSpinner"); // Hide spinner
          return reject(error);
        });
    });
  } 

  search(searchRequest) {
    let token = PortalStorage.getTokenFromCookies();
    if (token == null) {
      return Promise.reject("missing token");
    }
    // console.log("searach", searchRequest);
    return new Promise((resolve, reject) => {
      this.searchPromise(token, searchRequest)
        .then((resp) => {
          resolve(resp);
        })
        .catch((error) => {
          let e = JSON.parse(error);
          console.log("error", e);
          // Wenn kein Token vorhanden oder Session abgelaufen, erneut
          // versuchen...
          if (
            e.code == "error.user.session_not_found" ||
            e.code == "error.user.not_authenticated"
          ) {
            this.startSession(PortalL10n.lang).then((ret) => {
              let token = PortalStorage.getTokenFromCookies();
              this.searchPromise(token, searchRequest)
                .then((resp) => {
                  resolve(resp);
                })
                .catch((error) => {
                  // falls es dann immer noch nicht klappt: Abbruch
                  console.log(error);
                  reject;
                });
            });
          }
          reject;
        });
    });
  }

  searchPromise(token, searchRequest) {
    //start spinner
    PortalEvent.emit("showSpinner");
    return new Promise((resolve, reject) => {
      fetch(url([this.endpoint, "search"], { token: token }), {
        credentials: "same-origin",
        method: "POST",
        body: JSON.stringify(searchRequest),
      })
        .then((resp) => (resp.ok ? resp.json() : resp.text().then(reject)))
        .then((data) => {
          PortalEvent.emit("hideSpinner"); // Hide spinner
          return resolve(data);
        })
        .catch((error) => {
          PortalEvent.emit("hideSpinner"); // Hide spinner
          return reject(error);
        });
    });
  }

  object(obj_id) {
    let token = PortalStorage.getTokenFromCookies();
    if (token == null) {
      return Promise.reject("missing token");
    }
    let u = url([
      this.endpoint,
      "objects/id/" + obj_id + "/format/xslt/detail",
    ]);
    if (token) {
      u += "?token=" + token;
    }
    return new Promise((resolve, reject) => {
      fetch(u)
        .then((resp) => (resp.ok ? resp.text() : resp.text().then(reject)))
        .then((data) => {
          return resolve(data);
        })
        .catch(reject);
    });
  }

  object_json(obj_id) {
    let token = PortalStorage.getTokenFromCookies();
    if (token == null) {
      return Promise.reject("missing token");
    }
    let u = url([this.endpoint, "objects/id/" + obj_id]);
    if (token) {
      u += "?token=" + token;
    }
    return new Promise((resolve, reject) => {
      fetch(u)
        .then((resp) => (resp.ok ? resp.json() : resp.text().then(reject)))
        .then((data) => {
          return resolve(data);
        })
        .catch(reject);
    });
  }

  easydbL10N() {
    let token = PortalStorage.getTokenFromCookies();
    if (token == null) {
      return Promise.reject("missing token");
    }
    let u = url([this.endpoint, "l10n/user/CURRENT"]);
    if (token) {
      u += "?token=" + token;
    }
    return new Promise((resolve, reject) => {
      fetch(u)
        .then((resp) => (resp.ok ? resp.json() : resp.text().then(reject)))
        .then((data) => {
          return resolve(data);
        })
        .catch(reject);
    });
  }

  suggest(suggestRequest) {
    // Für suggest-Request wird davon ausgegangen, dass aktuelles Token
    // vorhanden ist!
    let token = PortalStorage.getTokenFromCookies();
    if (token == null) {
      return Promise.reject("missing token");
    }
    return new Promise((resolve, reject) => {
      fetch(
        url([this.endpoint, "suggest"], {
          token: token,
          BODY: JSON.stringify(suggestRequest),
        }),
        { credentials: "same-origin" }
      )
        .then((resp) => (resp.ok ? resp.json() : resp.text().then(reject)))
        .then((data) => {
          return resolve(data);
        })
        .catch(reject);
    });
  }

  static url(...args) {
    return url(...args);
  }
};



//////////////////
// WEBPACK FOOTER
// ./js/PortalApi.js
// module id = 1
// module chunks = 0